import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { C } from '../theme';

/* ── Animated number counter ─────────────────────────────────────────────── */
function Counter({ target, prefix = '', suffix = '', duration = 900 }) {
  const [val, setVal] = useState(0);
  const raf = useRef();
  useEffect(() => {
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * ease));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return <>{prefix}{val.toLocaleString()}{suffix}</>;
}

/* ── Smooth area/line chart (SVG) ────────────────────────────────────────── */
function AreaChart({ data, color = C.primary, height = 120 }) {
  const W = 400, H = height, pad = { t: 10, r: 10, b: 28, l: 10 };
  const cW = W - pad.l - pad.r;
  const cH = H - pad.t - pad.b;
  const max = Math.max(...data.map(d => d.amount), 1);
  const pts = data.map((d, i) => ({
    x: pad.l + (i / (data.length - 1)) * cW,
    y: pad.t + cH - (d.amount / max) * cH,
    ...d,
  }));

  // Smooth bezier path
  const curve = pts.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x},${p.y}`;
    const prev = pts[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return `${acc} C ${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;
  }, '');
  const area = `${curve} L ${pts[pts.length-1].x},${pad.t+cH} L ${pts[0].x},${pad.t+cH} Z`;
  const gradId = `grad-${color.replace('#','')}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* grid lines */}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <line key={f} x1={pad.l} y1={pad.t + cH * (1-f)} x2={W-pad.r} y2={pad.t + cH * (1-f)}
          stroke="#F3F4F6" strokeWidth="1" />
      ))}
      {/* area fill */}
      <path d={area} fill={`url(#${gradId})`} />
      {/* line */}
      <path d={curve} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* dots + labels */}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3.5" fill={color} opacity={p.amount > 0 ? 1 : 0.3} />
          <text x={p.x} y={H - 6} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontWeight={i===pts.length-1?'700':'400'}>
            {p.month}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ── Donut chart ─────────────────────────────────────────────────────────── */
function Donut({ segments, size = 120, label, sublabel }) {
  const R = 44, cx = size/2, cy = size/2, stroke = 10;
  const total = segments.reduce((s, g) => s + g.value, 0) || 1;
  const circ  = 2 * Math.PI * R;
  let offset  = circ * 0.25;   // start at top

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#F3F4F6" strokeWidth={stroke} />
      {segments.filter(s => s.value > 0).map((seg, i) => {
        const dash = (seg.value / total) * circ;
        const gap  = circ - dash;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={R}
            fill="none" stroke={seg.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset + circ * 0.25}
            strokeLinecap="butt"
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        );
        offset += dash;
        return el;
      })}
      {label && <>
        <text x={cx} y={cy - 5} textAnchor="middle" fontSize="14" fontWeight="800" fill={C.textMain}>{label}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="8" fill={C.textSub}>{sublabel}</text>
      </>}
    </svg>
  );
}

/* ── Radial progress ring ────────────────────────────────────────────────── */
function Ring({ pct, color, size = 56, label }) {
  const R = 22, circ = 2 * Math.PI * R;
  const dash = Math.min(pct, 100) / 100 * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={R} fill="none" stroke="#F3F4F6" strokeWidth="5" />
      <circle cx={size/2} cy={size/2} r={R} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ * 0.25}
        strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.8s ease' }} />
      <text x={size/2} y={size/2 + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill={color}>{label}</text>
    </svg>
  );
}

/* ── Horizontal bar ──────────────────────────────────────────────────────── */
function HBar({ segments, total }) {
  if (!total) return <div style={{ height: 8, borderRadius: 6, background: '#F3F4F6' }} />;
  return (
    <div style={{ display: 'flex', height: 8, borderRadius: 6, overflow: 'hidden', gap: 1 }}>
      {segments.filter(s => s.val > 0).map((s, i) => (
        <div key={i} style={{ flex: s.val, background: s.color, transition: 'flex 0.6s ease' }} title={`${s.label}: ${s.val}`} />
      ))}
    </div>
  );
}

/* ── Grouped bar chart: renewals due vs done, our vs dealer, by month ────── */
const RENEWAL_SERIES = [
  { key: 'due_our',     label: 'Due · Our Clients',     color: '#F59E0B' },
  { key: 'due_dealer',  label: 'Due · Dealer Clients',  color: '#8B5CF6' },
  { key: 'done_our',    label: 'Done · Our Clients',    color: '#10B981' },
  { key: 'done_dealer', label: 'Done · Dealer Clients', color: '#0891B2' },
];

function niceMax(v) {
  if (v <= 4) return 4;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  return Math.ceil(v / pow) * pow;
}

function RenewalBarChart({ labels, series }) {
  const groups  = labels.length;
  const groupW  = 60;                 // px per month group (chart scrolls if wide)
  const H       = 260;
  const pad     = { t: 14, r: 14, b: 42, l: 36 };
  const cW      = groups * groupW;
  const W       = pad.l + cW + pad.r;
  const cH      = H - pad.t - pad.b;
  const maxVal  = Math.max(1, ...RENEWAL_SERIES.flatMap(s => series[s.key] || []));
  const top     = niceMax(maxVal);
  const barGap  = 2;
  const innerPad= 8;
  const barW    = Math.max(4, (groupW - innerPad * 2 - barGap * (RENEWAL_SERIES.length - 1)) / RENEWAL_SERIES.length);
  const yTicks  = 4;
  const scaleY  = v => pad.t + cH - (v / top) * cH;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', minWidth: '100%' }}>
        {/* gridlines + y-axis labels */}
        {Array.from({ length: yTicks + 1 }).map((_, i) => {
          const y = pad.t + (cH * i / yTicks);
          return (
            <g key={i}>
              <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="#F3F4F6" strokeWidth="1" />
              <text x={pad.l - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#9CA3AF">
                {Math.round(top * (1 - i / yTicks))}
              </text>
            </g>
          );
        })}
        {/* month groups */}
        {labels.map((lbl, gi) => {
          const gx = pad.l + groupW * gi;
          const [mon, yr] = lbl.split(' ');
          return (
            <g key={gi}>
              {RENEWAL_SERIES.map((s, si) => {
                const v = (series[s.key] || [])[gi] || 0;
                const y = scaleY(v);
                const h = pad.t + cH - y;
                const x = gx + innerPad + si * (barW + barGap);
                return (
                  <rect key={s.key} x={x} y={y} width={barW} height={Math.max(0, h)} rx="1.5"
                    fill={s.color} opacity={v === 0 ? 0.12 : 1}>
                    <title>{`${lbl}\n${s.label}: ${v}`}</title>
                  </rect>
                );
              })}
              <text x={gx + groupW / 2} y={H - 26} textAnchor="middle" fontSize="9" fontWeight="600" fill="#6B7280">{mon}</text>
              {(gi === 0 || mon === 'Jan') &&
                <text x={gx + groupW / 2} y={H - 14} textAnchor="middle" fontSize="8" fill="#9CA3AF">{yr}</text>}
            </g>
          );
        })}
        <line x1={pad.l} y1={pad.t + cH} x2={W - pad.r} y2={pad.t + cH} stroke="#E5E7EB" strokeWidth="1.2" />
      </svg>
    </div>
  );
}

/* ── KPI card ────────────────────────────────────────────────────────────── */
function KpiCard({ label, value, sub, icon, gradient, delay = 0 }) {
  return (
    <div style={{
      background: gradient, borderRadius: 16, padding: '20px 22px',
      display: 'flex', flexDirection: 'column', gap: 6, position: 'relative', overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      animation: `fadeUp 0.4s ease ${delay}s both`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>{sub}</div>}
      <div style={{ position: 'absolute', right: 14, top: 12, fontSize: 32, opacity: 0.2 }}>{icon}</div>
    </div>
  );
}

/* ── Section header ──────────────────────────────────────────────────────── */
function SecHdr({ title, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 12, borderBottom: `1.5px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 3, height: 16, background: `linear-gradient(180deg,${C.primary},${C.primaryDark})`, borderRadius: 2 }} />
        <span style={{ fontSize: 14, fontWeight: 800, color: C.textMain }}>{title}</span>
      </div>
      {right && <span style={{ fontSize: 11, color: C.textSub, fontWeight: 500 }}>{right}</span>}
    </div>
  );
}

/* ── Panel wrapper ───────────────────────────────────────────────────────── */
const Pnl = ({ children, style }) => (
  <div style={{ background: C.white, borderRadius: 14, padding: 20, border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(245,166,35,0.05)', ...style }}>
    {children}
  </div>
);

/* ── Main dashboard ──────────────────────────────────────────────────────── */
export default function Dashboard() {
  const { logout } = useAuth();
  const navigate   = useNavigate();
  const [d,  setD]  = useState(null);
  const [rc, setRc] = useState(null);          // renewal chart (due vs done, monthly)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/dashboard')
      .then(r => setD(r.data))
      .catch(() => { logout(); navigate('/login'); })
      .finally(() => setLoading(false));
  }, [logout, navigate]);

  useEffect(() => {
    api.get('/api/dashboard/renewal-chart').then(r => setRc(r.data)).catch(() => {});
  }, []);

  if (loading) return <LoadingScreen />;
  if (!d) return null;

  const fmtRs = n =>
    n >= 10_00_000 ? `₹${(n/100000).toFixed(1)}L`
    : n >= 1000    ? `₹${(n/1000).toFixed(1)}K`
    :                `₹${n.toLocaleString()}`;

  const issueTotal = (d.service.open + d.complaints.open + d.service.in_progress + d.complaints.in_progress);
  const invUsed    = d.inventory.installed + d.inventory.dealer_stock;
  const invUsedPct = d.inventory.total ? Math.round(invUsed / d.inventory.total * 100) : 0;
  const svcClosePct= d.service.total ? Math.round((d.service.resolved + d.service.closed) / d.service.total * 100) : 0;
  const cmpClosePct= d.complaints.total ? Math.round((d.complaints.resolved + d.complaints.closed) / d.complaints.total * 100) : 0;

  return (
    <Layout title="Dashboard">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.5} }
      `}</style>

      {/* ── Hero banner ─────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, #1C1407 0%, #2E1C08 50%, #1C1407 100%)`,
        borderRadius: 18, padding: '26px 32px', marginBottom: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20,
        position: 'relative', overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        {/* glow */}
        <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: `${C.primary}15`, filter: 'blur(60px)', top: -80, right: 100, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 4, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{d.today}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>
            {greeting()}, <span style={{ color: C.primary }}>{d.user}</span> 👋
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Here's your live business overview
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          {[
            { label: 'Clients',   val: d.clients.total,   col: '#60A5FA' },
            { label: 'Dealers',   val: d.dealers.total,   col: C.primary },
            { label: 'Inventory', val: d.inventory.total, col: '#A78BFA' },
            { label: 'Open Issues', val: issueTotal,      col: issueTotal > 0 ? '#F87171' : '#34D399' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '10px 18px', border: '1px solid rgba(255,255,255,0.1)', minWidth: 72 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: s.col }}>{s.val}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{ position: 'absolute', right: 24, bottom: -10, fontSize: 90, opacity: 0.05, userSelect: 'none' }}>🐝</div>
      </div>

      {/* ── KPI cards ───────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14, marginBottom: 20 }}>
        <KpiCard label="Revenue Collected" value={fmtRs(d.payments.collected)} sub="All time" icon="💰"
          gradient="linear-gradient(135deg,#059669,#10B981)" delay={0} />
        <KpiCard label="This Month" value={fmtRs(d.payments.this_month)} sub={d.monthly_revenue.slice(-1)[0]?.month} icon="📅"
          gradient="linear-gradient(135deg,#2563EB,#3B82F6)" delay={0.05} />
        <KpiCard label="Pending Balance" value={fmtRs(d.payments.pending)} sub="To collect" icon="⏳"
          gradient="linear-gradient(135deg,#D97706,#F59E0B)" delay={0.1} />
        <KpiCard label="Active Sales" value={d.mappings.active} sub={`${d.mappings.completed} completed`} icon="🗺️"
          gradient={`linear-gradient(135deg,${C.primaryDeep},${C.primary})`} delay={0.15} />
        <KpiCard label="Renewals This Month" value={d.renewals.this_month} sub={`${d.renewals.total} total · ${d.renewals.completed} done`} icon="🔄"
          gradient="linear-gradient(135deg,#0891B2,#06B6D4)" delay={0.2} />
        <KpiCard label="Dealer Mappings" value={d.dealer_mappings.total} sub={`${d.dealer_mappings.active} active`} icon="🗂️"
          gradient="linear-gradient(135deg,#7C3AED,#8B5CF6)" delay={0.25} />
        <KpiCard label="Replacements" value={d.replacements} sub="Total done" icon="🔃"
          gradient="linear-gradient(135deg,#DB2777,#EC4899)" delay={0.3} />
      </div>

      {/* ── Revenue chart + Inventory donut ─────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, marginBottom: 16 }}>

        {/* Area chart */}
        <Pnl>
          <SecHdr title="Monthly Revenue Trend"
            right={`Peak: ${fmtRs(Math.max(...d.monthly_revenue.map(m => m.amount)))}`} />
          <div style={{ height: 150 }}>
            <AreaChart data={d.monthly_revenue} color={C.primary} height={150} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            {d.monthly_revenue.map((m, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: i === d.monthly_revenue.length - 1 ? C.primaryDeep : C.textMain }}>
                  {m.amount > 0 ? fmtRs(m.amount) : '—'}
                </div>
              </div>
            ))}
          </div>
        </Pnl>

        {/* Inventory donut */}
        <Pnl>
          <SecHdr title="Inventory" right={`${d.inventory.total} total`} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <Donut
              segments={[
                { value: d.inventory.in_stock,     color: '#3B82F6' },
                { value: d.inventory.dealer_stock, color: '#8B5CF6' },
                { value: d.inventory.installed,    color: '#10B981' },
                { value: Math.max(0, d.inventory.total - d.inventory.in_stock - d.inventory.dealer_stock - d.inventory.installed), color: '#E5E7EB' },
              ]}
              size={130} label={d.inventory.total} sublabel="Items"
            />
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                { label: 'In Stock',     val: d.inventory.in_stock,     color: '#3B82F6', bg: '#DBEAFE' },
                { label: 'Dealer Stock', val: d.inventory.dealer_stock, color: '#8B5CF6', bg: '#EDE9FE' },
                { label: 'Installed',    val: d.inventory.installed,    color: '#10B981', bg: '#D1FAE5' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
                    <span style={{ fontSize: 12, color: C.textSub }}>{s.label}</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 10, background: s.bg, color: s.color }}>{s.val}</span>
                </div>
              ))}
            </div>
          </div>
        </Pnl>
      </div>

      {/* ── Renewals: due vs completed, our vs dealer, by month ──────────── */}
      {rc && rc.labels && (
        <Pnl style={{ marginBottom: 16 }}>
          <SecHdr title="Renewals — Due vs Completed (monthly)"
            right={`Due ${rc.totals.due_our + rc.totals.due_dealer} · Done ${rc.totals.done_our + rc.totals.done_dealer}`} />
          {/* Legend with per-series totals */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
            {RENEWAL_SERIES.map(s => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color }} />
                <span style={{ fontSize: 11.5, color: C.textSub }}>{s.label}</span>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: C.textMain }}>{rc.totals[s.key]}</span>
              </div>
            ))}
          </div>
          <RenewalBarChart labels={rc.labels} series={rc.series} />
          <div style={{ fontSize: 10.5, color: C.textSub, marginTop: 8 }}>
            Window: 6 months back → 12 months ahead · “Our” = direct clients, “Dealer” = dealer-managed clients.
          </div>
        </Pnl>
      )}

      {/* ── Module status cards row ──────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14, marginBottom: 16 }}>

        {/* Service */}
        <Pnl>
          <SecHdr title="Service Requests" right={`${d.service.total} total`} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
            <Ring pct={svcClosePct} color="#10B981" size={60} label={`${svcClosePct}%`} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: C.textSub, marginBottom: 4 }}>Resolution rate</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#10B981' }}>{d.service.resolved + d.service.closed} resolved</div>
            </div>
          </div>
          <HBar total={d.service.total} segments={[
            { val: d.service.open, color: '#3B82F6', label: 'Open' },
            { val: d.service.in_progress, color: '#F59E0B', label: 'In Progress' },
            { val: d.service.resolved, color: '#10B981', label: 'Resolved' },
            { val: d.service.closed, color: '#9CA3AF', label: 'Closed' },
          ]} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {[['Open', d.service.open, '#3B82F6', '#DBEAFE'], ['In Progress', d.service.in_progress, '#F59E0B', '#FEF3C7'], ['Resolved', d.service.resolved, '#10B981', '#D1FAE5']].map(([l, v, c, bg]) => (
              <div key={l} style={{ flex: 1, textAlign: 'center', padding: '6px 4px', borderRadius: 8, background: bg }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{v}</div>
                <div style={{ fontSize: 9, color: c, fontWeight: 600 }}>{l}</div>
              </div>
            ))}
          </div>
        </Pnl>

        {/* Complaints */}
        <Pnl>
          <SecHdr title="Complaints" right={`${d.complaints.total} total`} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
            <Ring pct={cmpClosePct} color="#EF4444" size={60} label={`${cmpClosePct}%`} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: C.textSub, marginBottom: 4 }}>Resolution rate</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#EF4444' }}>{d.complaints.resolved + d.complaints.closed} resolved</div>
            </div>
          </div>
          <HBar total={d.complaints.total} segments={[
            { val: d.complaints.open, color: '#EF4444', label: 'Open' },
            { val: d.complaints.in_progress, color: '#F59E0B', label: 'In Progress' },
            { val: d.complaints.resolved, color: '#10B981', label: 'Resolved' },
            { val: d.complaints.closed, color: '#9CA3AF', label: 'Closed' },
          ]} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {[['Open', d.complaints.open, '#EF4444', '#FEE2E2'], ['In Progress', d.complaints.in_progress, '#F59E0B', '#FEF3C7'], ['Resolved', d.complaints.resolved, '#10B981', '#D1FAE5']].map(([l, v, c, bg]) => (
              <div key={l} style={{ flex: 1, textAlign: 'center', padding: '6px 4px', borderRadius: 8, background: bg }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{v}</div>
                <div style={{ fontSize: 9, color: c, fontWeight: 600 }}>{l}</div>
              </div>
            ))}
          </div>
        </Pnl>

        {/* Purchase Orders */}
        <Pnl>
          <SecHdr title="Purchase Orders" right={`${d.purchase_orders.total} total`} />
          <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#10B981' }}>{d.purchase_orders.received}</div>
              <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, marginTop: 2 }}>Received</div>
            </div>
            <div style={{ width: 1, background: C.border }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#F59E0B' }}>{d.purchase_orders.pending}</div>
              <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, marginTop: 2 }}>Pending</div>
            </div>
          </div>
          <HBar total={d.purchase_orders.total} segments={[
            { val: d.purchase_orders.received, color: '#10B981', label: 'Received' },
            { val: d.purchase_orders.pending,  color: '#F59E0B', label: 'Pending'  },
          ]} />
          {/* Renewals summary */}
          <div style={{ marginTop: 16, padding: '14px', borderRadius: 10, background: 'linear-gradient(135deg,#ECFEFF,#E0F2FE)', border: '1.5px solid #BAE6FD' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>🔄 Renewals</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, color: '#0891B2', lineHeight: 1 }}>{d.renewals.this_month}</div>
                <div style={{ fontSize: 10, color: '#0369A1', marginTop: 2 }}>This Month</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0891B2' }}>{d.renewals.total} total</div>
                <div style={{ fontSize: 10, color: '#0369A1' }}>{d.renewals.completed} completed</div>
              </div>
            </div>
          </div>
        </Pnl>

        {/* Dealers */}
        <Pnl>
          <SecHdr title="Dealers & Clients" />
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', borderRadius: 12, background: `${C.primary}15`, border: `1.5px solid ${C.primary}40` }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: C.primaryDeep }}>{d.dealers.total}</div>
              <div style={{ fontSize: 10, color: C.primaryDeep, fontWeight: 700 }}>DEALERS</div>
              <div style={{ fontSize: 10, color: C.textSub, marginTop: 2 }}>{d.dealers.active} active</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', borderRadius: 12, background: '#EDE9FE', border: '1.5px solid #DDD6FE' }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#6D28D9' }}>{d.clients.dealer}</div>
              <div style={{ fontSize: 10, color: '#6D28D9', fontWeight: 700 }}>DEALER CLIENTS</div>
              <div style={{ fontSize: 10, color: C.textSub, marginTop: 2 }}>{d.dealer_mappings.total} mapped</div>
            </div>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: '#F9FAFB', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, color: C.textSub, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Admin Clients</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.textMain }}>{d.clients.total}</div>
            <HBar total={d.mappings.total || 1} segments={[
              { val: d.mappings.completed, color: '#10B981', label: 'Completed' },
              { val: d.mappings.active,    color: C.primary, label: 'Active' },
            ]} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
              <span style={{ fontSize: 10, color: '#10B981', fontWeight: 600 }}>{d.mappings.completed} completed</span>
              <span style={{ fontSize: 10, color: C.primary, fontWeight: 600 }}>{d.mappings.active} active</span>
            </div>
          </div>
        </Pnl>
      </div>

      {/* ── Inventory Stock by Category ─────────────────────────────────── */}
      <Pnl style={{ marginBottom: 16 }}>
        <SecHdr title="Inventory Stock by Category"
          right={`${d.inventory.in_stock} in-stock of ${d.inventory.total} total`} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
          {d.inventory.by_category.map((cat, i) => {
            const COLORS = [
              { main: '#3B82F6', stock: '#DBEAFE', installed: '#93C5FD', dealer: '#BFDBFE', text: '#1E40AF' },
              { main: '#8B5CF6', stock: '#EDE9FE', installed: '#C4B5FD', dealer: '#DDD6FE', text: '#5B21B6' },
              { main: '#10B981', stock: '#D1FAE5', installed: '#6EE7B7', dealer: '#A7F3D0', text: '#065F46' },
              { main: '#F59E0B', stock: '#FEF3C7', installed: '#FCD34D', dealer: '#FDE68A', text: '#92400E' },
              { main: '#EF4444', stock: '#FEE2E2', installed: '#FCA5A5', dealer: '#FECACA', text: '#991B1B' },
            ];
            const col = COLORS[i % COLORS.length];
            const stockPct     = cat.total ? Math.round(cat.in_stock / cat.total * 100)     : 0;
            const installedPct = cat.total ? Math.round(cat.installed / cat.total * 100)    : 0;
            const dealerPct    = cat.total ? Math.round(cat.dealer_stock / cat.total * 100) : 0;
            return (
              <div key={cat.name} style={{ background: col.stock, borderRadius: 12, padding: '16px', border: `1.5px solid ${col.installed}` }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: col.text }}>{cat.name}</div>
                    <div style={{ fontSize: 11, color: col.text, opacity: 0.7, marginTop: 1 }}>{cat.total} items total</div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: col.main }}>{cat.in_stock}</div>
                </div>
                {/* Stacked bar */}
                <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', marginBottom: 10, gap: 1, background: '#ffffff60' }}>
                  {cat.in_stock    > 0 && <div style={{ flex: cat.in_stock,    background: col.main,      borderRadius: '6px 0 0 6px' }} title={`In Stock: ${cat.in_stock}`} />}
                  {cat.installed   > 0 && <div style={{ flex: cat.installed,   background: col.installed                            }} title={`Installed: ${cat.installed}`} />}
                  {cat.dealer_stock> 0 && <div style={{ flex: cat.dealer_stock,background: col.dealer,    borderRadius: '0 6px 6px 0' }} title={`Dealer: ${cat.dealer_stock}`} />}
                </div>
                {/* Legend rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[
                    { label: 'In Stock',      val: cat.in_stock,     pct: stockPct,     color: col.main      },
                    { label: 'Installed',     val: cat.installed,    pct: installedPct, color: col.installed  },
                    { label: 'Dealer Stock',  val: cat.dealer_stock, pct: dealerPct,    color: col.dealer     },
                  ].map(r => (
                    <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: r.color }} />
                        <span style={{ fontSize: 11, color: col.text, opacity: 0.8 }}>{r.label}</span>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: col.text }}>{r.val} <span style={{ fontWeight: 400, opacity: 0.6 }}>({r.pct}%)</span></span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Pnl>

      {/* ── Top clients + Recent activity ────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, marginBottom: 16 }}>

        {/* Top clients */}
        <Pnl>
          <SecHdr title="Top Clients" right="by revenue" />
          {d.top_clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0', color: C.textSub, fontSize: 13 }}>No payment data</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {d.top_clients.map((c, i) => {
                const maxAmt = d.top_clients[0].amount;
                const pct    = Math.round(c.amount / maxAmt * 100);
                const colors = [C.primary, '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B'];
                const col    = colors[i];
                return (
                  <div key={c.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, background: `${col}20`, color: col, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {i + 1}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.textMain }}>{c.name}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: col }}>
                        {c.amount >= 1000 ? `₹${(c.amount/1000).toFixed(1)}K` : `₹${c.amount}`}
                      </span>
                    </div>
                    <div style={{ height: 5, borderRadius: 4, background: '#F3F4F6', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 4, transition: 'width 0.8s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Pnl>

        {/* Recent activity */}
        <Pnl>
          <SecHdr title="Recent Activity" right={`${d.recent_activity.length} events`} />
          {d.recent_activity.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0', color: C.textSub, fontSize: 13 }}>No recent activity.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {d.recent_activity.map((a, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                  borderBottom: i < d.recent_activity.length - 1 ? `1px solid ${C.border}` : 'none',
                }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                    {a.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.textMain, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.text}</div>
                    <div style={{ fontSize: 11, color: C.textSub, marginTop: 1 }}>{a.sub}</div>
                  </div>
                  <div style={{ fontSize: 10, color: C.textSub, whiteSpace: 'nowrap', flexShrink: 0 }}>{a.time}</div>
                </div>
              ))}
            </div>
          )}
        </Pnl>
      </div>

    </Layout>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
}

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#1C1407', gap: 16 }}>
      <div style={{ fontSize: 44 }}>🐝</div>
      <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,0.12)', borderTopColor: C.primary, borderRadius: '50%', animation: 'spin 0.75s linear infinite' }} />
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, margin: 0 }}>Loading workspace…</p>
    </div>
  );
}
